/**
 * Feb 2026 R293zx · Share-by-link control dialog.
 *
 * Lets the author mint / rotate / revoke a public link for the current
 * document, pick an expiry, copy the URL to the clipboard, and see how
 * many times it's been opened. Backed by:
 *   POST   /gst/workspaces/{ws}/documents/{did}/share
 *   GET    /gst/workspaces/{ws}/documents/{did}/share
 *   DELETE /gst/workspaces/{ws}/documents/{did}/share
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Share2, Copy, Link as LinkIcon, Eye, Trash2, Loader2, MessageCircle, Phone, History } from "lucide-react";

const EXPIRIES = [
  { value: "7",   label: "7 days" },
  { value: "30",  label: "30 days", recommended: true },
  { value: "90",  label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "0",   label: "No expiry" },
];

function _shareUrl(token) {
  return `${window.location.origin}/share/${token}`;
}

// Feb 2026 R293zy · Normalise a phone number for wa.me — strip +, spaces,
// dashes, parens. We purposefully do not validate country codes; the
// author sees what they're sending in the editable input.
function _digits(phone) {
  return (phone || "").replace(/[^0-9]/g, "");
}

export default function ShareLinkDialog({ ws, doc, onClose }) {
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);  // {active, token, url, expires_at, views, ...}
  const [customerPhone, setCustomerPhone] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);  // null = not loaded, [] = empty, [...] = loaded
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    if (!ws || !doc?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/share`);
        if (!cancelled) {
          setShare(r.data?.active ? r.data : null);
          if (r.data?.customer_phone) setCustomerPhone(r.data.customer_phone);
        }
      } catch (e) {
        // non-blocking
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ws, doc?.id]);

  const loadLog = async () => {
    try {
      const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/share/log`);
      setLog(r.data?.entries || []);
    } catch (e) {
      setLog([]);
    }
  };

  const mint = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/share`, {
        expires_days: parseInt(expiry, 10) || null,
      });
      setShare({
        active: true,
        token: r.data.token,
        url: _shareUrl(r.data.token),
        expires_at: r.data.expires_at,
        views: 0,
      });
      if (r.data?.customer_phone && !customerPhone) {
        setCustomerPhone(r.data.customer_phone);
      }
      toast.success(share ? "Link regenerated — the old link no longer works." : "Share link created.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!share) return;
    setBusy(true);
    try {
      await api.delete(`/gst/workspaces/${ws}/documents/${doc.id}/share`);
      setShare(null);
      toast.success("Share link revoked.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(_shareUrl(share.token));
      toast.success("Link copied.");
    } catch (e) {
      toast.error("Couldn't copy. Select the link and copy manually.");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="gst-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Share {doc?.doc_no || "document"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Anyone with the link can open the PDF and any attachments — no login required.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking…
          </div>
        ) : share ? (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800">
                <LinkIcon className="h-3.5 w-3.5" /> Active share link
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  readOnly
                  value={_shareUrl(share.token)}
                  className="flex-1 h-8 text-xs px-2 bg-white border border-emerald-300 rounded focus:outline-none"
                  data-testid="gst-share-url"
                  onClick={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={copy} data-testid="gst-share-copy">
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-emerald-800">
                <span className="inline-flex items-center gap-1">
                  <Eye className="h-3 w-3" /> {share.views || 0} view{(share.views || 0) === 1 ? "" : "s"}
                </span>
                <span>
                  {share.expires_at
                    ? `Expires ${new Date(share.expires_at).toLocaleDateString()}`
                    : "No expiry"}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <Select value={expiry} onValueChange={setExpiry} disabled={busy}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="gst-share-expiry">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRIES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={mint} disabled={busy}
                        data-testid="gst-share-regenerate">
                  Regenerate
                </Button>
              </div>
              <Button size="sm" variant="ghost" onClick={revoke} disabled={busy}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      data-testid="gst-share-revoke">
                <Trash2 className="h-3 w-3 mr-1" /> Revoke
              </Button>
            </div>

            {/* Feb 2026 R293zy · WhatsApp push row — pre-filled with the
                customer's phone (looked up from clients). Editable so
                the author can override before clicking Send. Opens
                wa.me which lets WhatsApp Web / mobile pick up. */}
            <div className="rounded-md border border-border p-3 bg-muted/30">
              <div className="flex items-center gap-2 text-xs font-semibold mb-2">
                <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                Send on WhatsApp
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center flex-1 min-w-0 border border-border rounded bg-background px-2">
                  <Phone className="h-3 w-3 text-muted-foreground mr-1 shrink-0" />
                  <input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="h-8 w-full text-xs bg-transparent focus:outline-none"
                    data-testid="gst-share-wa-phone"
                  />
                </div>
                {(() => {
                  const digits = _digits(customerPhone);
                  const docKind = (doc?.doc_type || "document").replace("_", " ");
                  const msg = `Hi — here's the ${docKind} ${doc?.doc_no || ""}: ${_shareUrl(share.token)}`;
                  const href = digits
                    ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`
                    : `https://wa.me/?text=${encodeURIComponent(msg)}`;
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid="gst-share-wa-btn"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      {digits ? "Send" : "Pick contact"}
                    </a>
                  );
                })()}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Opens WhatsApp with the link already typed — you still tap send.
              </div>
            </div>

            {/* Feb 2026 R293zy · Access log — collapsible list of
                anonymous opens so the author knows which recipient
                actually looked at the doc. */}
            <div>
              <button
                type="button"
                onClick={() => {
                  const next = !showLog;
                  setShowLog(next);
                  if (next && log === null) loadLog();
                }}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                data-testid="gst-share-log-toggle"
              >
                <History className="h-3 w-3" />
                {showLog ? "Hide" : "Show"} access log ({share.views || 0})
              </button>
              {showLog && (
                <div className="mt-2 border border-border rounded-md bg-background max-h-48 overflow-auto">
                  {log === null && (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Loading…
                    </div>
                  )}
                  {log !== null && log.length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                      No views yet. The customer hasn't opened the link.
                    </div>
                  )}
                  {log !== null && log.length > 0 && (
                    <ul className="divide-y divide-border">
                      {log.map((e, i) => (
                        <li key={i} className="px-3 py-2 text-[11px]" data-testid={`gst-share-log-row-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-muted-foreground">
                              {e.ip || "unknown ip"}
                            </span>
                            <span className="text-muted-foreground">
                              {e.viewed_at ? new Date(e.viewed_at).toLocaleString() : ""}
                            </span>
                          </div>
                          {e.user_agent && (
                            <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={e.user_agent}>
                              {e.user_agent}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Create a signed link so the customer can see this document, download the PDF and
              open attachments — all without an account.
            </p>
            <div className="flex items-center gap-2">
              <Select value={expiry} onValueChange={setExpiry} disabled={busy}>
                <SelectTrigger className="h-9 flex-1 text-xs" data-testid="gst-share-expiry-create">
                  <SelectValue placeholder="Pick expiry" />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRIES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={mint} disabled={busy} data-testid="gst-share-create">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4 mr-1" />}
                Create link
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground">
              You can revoke or regenerate the link anytime. Only this workspace's members can mint links.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
